/**
 * P5's `code` → something a player can act on, in the language they are reading.
 *
 * The API contract is explicit that `message` is "player-safe English (the client localizes from
 * `code`; `message` is the fallback and the developer's hint)". So the client branches on
 * `code` and only falls back to `message` for a code it has never seen — which, for a
 * closed set, means a BE that added one.
 *
 * **The strings moved to the bundle** when the i18n pass wired `t()`. The mapping that is left is
 * the interesting half: `ApiErrorCode` → `ui.error.<code>`, one key per code, so a code
 * added on the backend fails the FE build at the `satisfies` below rather than rendering
 * the developer's English hint to a player.
 */
import { ApiError, type ApiErrorCode } from '@/api/client';
import type { UiKey } from '@/i18n/en.generated';
import type { Translate } from '@/i18n/t';

const KEYS = {
  // P7 makes this one avoidable — the budget is on the profile, so the control should
  // already be disabled. Reaching it means another tab spent the last slot.
  story_limit_reached: 'ui.error.story_limit_reached',
  story_not_found: 'ui.error.story_not_found',
  story_completed: 'ui.error.story_completed',
  story_already_started: 'ui.error.story_already_started',
  turn_in_progress: 'ui.error.turn_in_progress',
  unauthenticated: 'ui.error.unauthenticated',
  forbidden: 'ui.error.forbidden',
  // The two play gates. Both are avoidable the same way `story_limit_reached` is — the
  // profile carries them, so the control should already be gone — and both are reachable
  // anyway from a second tab, or from a budget that ran out mid-session.
  account_inactive: 'ui.error.account_inactive',
  usage_limit_reached: 'ui.error.usage_limit_reached',
  // The server that answered is being replaced (a deploy); see `src/quest-v2-api/README.md`,
  // the drain. Retryable, and safe to retry with the same Idempotency-Key — another task is
  // already taking the traffic.
  service_draining: 'ui.error.service_draining',

  // The five TurnAborted codes. A turn that aborted cost nothing and wrote nothing.
  refused: 'ui.error.refused',
  iteration_cap: 'ui.error.iteration_cap',
  empty_narration: 'ui.error.empty_narration',

  validation_error: 'ui.error.validation_error',
  internal: 'ui.error.internal',

  // Client-side codes (client.ts) — never from the wire.
  network: 'ui.error.network',
  malformed_response: 'ui.error.malformed_response',
  // The opposite of "your turn failed": the server finishes and commits a turn
  // whose client went away, so the honest instruction is to look, not to act again.
  stream_interrupted: 'ui.error.stream_interrupted',
} satisfies Record<ApiErrorCode, UiKey>;

export function describeApiError(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) {
    return t('ui.error.unknown');
  }
  const key = KEYS[error.code] as UiKey | undefined;
  return key === undefined ? error.message : t(key);
}

/**
 * Whether to offer the "report this" affordance, which pre-fills the feedback form with
 * `request_id`. Only worth showing when there is a trace to report and the failure is
 * ours — a player who is simply out of story slots has nothing to report.
 */
export function isReportable(error: unknown): boolean {
  return error instanceof ApiError && error.requestId !== null && error.status >= 500;
}
